import React from 'react';
import {
  GalleryWrapper,
  HeroSection,
  ViewAllMediaButton,
  MediaGrid,
  MediaTile,
  MediaImage,
  IframeWrapper,
  StyledIframe,
  ViewMoreButton,
} from './Styles';
import MediaIcon from '../Icons/MediaIcon';
import DirectionalArrow from '../Icons/DirectionalArrow';
import VideoTile from './VideoTile';

export const getDynamicURLClass = (url: string) => {
  if (url.includes('hft')) return 'rotate-front';
  if (url.includes('hbk')) return 'rotate-back';
  if (url.includes('fbd')) return 'remove-padding';
  if (url.includes('cbr')) return 'push-right';
  if (url.includes('.jpg')) return 'remove-shadow';
  return '';
};

interface MasonryGalleryProps {
  mediaItems: any[];
  isDesktopHidden: boolean;
  handleTileClick: (index: number) => void;
  handleGalleryClick: () => void;
  showMore: boolean;
  handleToggleShowMore: () => void;
  marketingVideoPreview: string | null;
  videoIndex: number;
  iframeDimensions: { widthPercentage: number; paddingTopPercentage: number };
  thirdRowRef: React.RefObject<HTMLDivElement>;
}

const MasonryGallery: React.FC<MasonryGalleryProps> = ({
  mediaItems,
  isDesktopHidden,
  handleTileClick,
  handleGalleryClick,
  showMore,
  handleToggleShowMore,
  marketingVideoPreview,
  videoIndex,
  iframeDimensions,
  thirdRowRef
}) => {
  if (!mediaItems || mediaItems.length === 0) {
    return <p>No media available</p>;
  }

  const heroImage = mediaItems[0];
  const gridImages = mediaItems.slice(1);
  const iFrameData = mediaItems.find(item => item.type === 'video' && item.url.includes('youtube'));

  // Determine where to insert the iframe video
  let iFramePosition;
  if (mediaItems.length < 6) {
    if (mediaItems.length % 2 !== 0) {
      iFramePosition = mediaItems.length - 1;
    } else {
      iFramePosition = mediaItems.length - 2;
    }
  } else {
    iFramePosition = 5;
  }

  const newGridImages = [...gridImages];
  if (iFrameData && !marketingVideoPreview) {
    // add the iframe video to the grid if no marketing video preview exists
    newGridImages.splice(iFramePosition, 0, { type: 'iframe', url: iFrameData.url });
  }

  let currentRowCount = 0;

  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrlHero = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=1500/${url}` : `${url}?width=500`;
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=800/${url}` : `${url}?width=300`;
  };

  return (
    <GalleryWrapper isDesktopHidden={isDesktopHidden}>
      {/* Hero Image */}
      <HeroSection urlClass={getDynamicURLClass(heroImage.url)} onClick={() => handleTileClick(0)}>
        <img loading='eager' src={getResizedImageUrlHero(heroImage.url)} alt="Hero" />
        <ViewAllMediaButton onClick={handleGalleryClick}>
          <MediaIcon />
          All media
        </ViewAllMediaButton>
      </HeroSection>

      <MediaGrid>
        {newGridImages.map((item, index) => {
          if (item.type === 'video' && marketingVideoPreview && item.url.includes('youtube')) {
            currentRowCount += 0.5;
            return (
              <VideoTile
                key={index}
                marketingVideoPreview={marketingVideoPreview}
                index={index}
                handleTileClick={()=>handleTileClick(index + 1)}
              />
            );
          }

          if (iFrameData && index === iFramePosition && !marketingVideoPreview) {
            currentRowCount++;
            return (
              <IframeWrapper key="iframe" ref={currentRowCount === 3 ? thirdRowRef : null}>
                <StyledIframe
                  src={iFrameData.url}
                  title="Product Video"
                  width={iframeDimensions.widthPercentage}
                  height={iframeDimensions.paddingTopPercentage}
                />
              </IframeWrapper>
            );
          }

          const rotationClass = getDynamicURLClass(item.url);
          if (item.type === 'image') {
            currentRowCount += 0.5;
            return (
              <MediaTile
                key={index}
                ref={currentRowCount === 3 ? thirdRowRef : null}
                className={currentRowCount > 3 && !showMore ? 'hide' : ''}
                onClick={currentRowCount > 3 ? (() => handleTileClick(index)) : (()=>handleTileClick(index + 1))}
              >
                <MediaImage src={getResizedImageUrl(item.url)} alt={`Tile ${index + 1}`} urlClass={rotationClass} loading={currentRowCount > 2 ? 'lazy' : 'eager'}/>
              </MediaTile>
            );
          }
          return null;
        })}
      </MediaGrid>

      {currentRowCount > 3 && (
        <ViewMoreButton onClick={handleToggleShowMore}>
          <span>
            {showMore ? 'View less' : 'View more'}
            <DirectionalArrow direction={showMore ? 'up' : 'down'} width={14} height={14} />
          </span>
        </ViewMoreButton>
      )}
    </GalleryWrapper>
  );
};

export default MasonryGallery;
